import React, { memo } from 'react';
import { cn } from '@/lib/utils';

/**
 * Partnr's reply markdown (headings, lists, bold, italic, inline code) as React
 * elements instead of an HTML string. Same subset and classes as the old
 * regex renderer, and model text is never injected as HTML.
 *
 * While `animate` is on, every word is its own span keyed by its position in
 * the reply. A streamed chunk only appends words, so React keeps the spans it
 * already has and the CSS blur-in runs once, on the new ones.
 */

interface WordCounter {
  next: number;
}

function words(text: string, counter: WordCounter, animate: boolean): React.ReactNode[] {
  if (!animate) return [text];
  return text.split(/(\s+)/).map((part) => {
    if (part === '' || /^\s+$/.test(part)) return part;
    const index = counter.next++;
    return (
      <span key={`w${index}`} className="partnr-blur-word">
        {part}
      </span>
    );
  });
}

// Same order as the old renderer: bold, then italic, then inline code
const INLINE_RE = /\*\*(.+?)\*\*|\*(.+?)\*|`([^`]+)`/g;
// With links on, [label](https://...) comes first so its label keeps its own emphasis
const INLINE_LINK_RE = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)|\*\*(.+?)\*\*|\*(.+?)\*|`([^`]+)`/g;

function inline(text: string, counter: WordCounter, animate: boolean, links = false): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  let last = 0;
  let k = 0;
  for (const match of text.matchAll(links ? INLINE_LINK_RE : INLINE_RE)) {
    const start = match.index ?? 0;
    if (start > last) out.push(...words(text.slice(last, start), counter, animate));
    const [label, href, bold, italic, code] = links ? match.slice(1) : [undefined, undefined, ...match.slice(1)];
    if (label !== undefined && href !== undefined) {
      out.push(
        <a key={`a${k++}`} href={href} target="_blank" rel="noopener noreferrer" className="underline decoration-line underline-offset-[3px] hover:decoration-current">
          {words(label, counter, animate)}
        </a>,
      );
    } else if (bold !== undefined) {
      out.push(<strong key={`b${k++}`}>{words(bold, counter, animate)}</strong>);
    } else if (italic !== undefined) {
      out.push(<em key={`i${k++}`}>{words(italic, counter, animate)}</em>);
    } else {
      out.push(
        <code key={`c${k++}`} className="bg-muted px-1 py-0.5 rounded text-xs font-mono">
          {words(code, counter, animate)}
        </code>,
      );
    }
    last = start + match[0].length;
  }
  if (last < text.length) out.push(...words(text.slice(last), counter, animate));
  return out;
}

function line(text: string, key: number, counter: WordCounter, animate: boolean, links = false): React.ReactNode {
  let m: RegExpMatchArray | null;
  if ((m = text.match(/^### (.*)$/))) {
    return <h3 key={key} className="text-sm font-semibold mt-2 mb-1">{inline(m[1], counter, animate, links)}</h3>;
  }
  if ((m = text.match(/^## (.*)$/))) {
    return <h2 key={key} className="text-base font-semibold mt-2 mb-1">{inline(m[1], counter, animate, links)}</h2>;
  }
  if ((m = text.match(/^# (.*)$/))) {
    return <h1 key={key} className="text-lg font-bold mt-2 mb-1">{inline(m[1], counter, animate, links)}</h1>;
  }
  if ((m = text.match(/^(?:- |• )(.*)$/))) {
    return <li key={key} className="ml-4 list-disc">{inline(m[1], counter, animate, links)}</li>;
  }
  if ((m = text.match(/^\d+\. (.*)$/))) {
    return <li key={key} className="ml-4 list-decimal">{inline(m[1], counter, animate, links)}</li>;
  }
  return <React.Fragment key={key}>{inline(text, counter, animate, links)}</React.Fragment>;
}

interface PartnrMarkdownProps {
  content: string;
  /** Blur in words as they arrive. Off for replies that are already complete. */
  animate?: boolean;
  /** Render [label](https://...) as links. Off by default (the old chat window shows them raw). */
  links?: boolean;
  className?: string;
}

const BLOCK_LINE_RE = /^(?:#{1,3} |- |• |\d+\. )/;

/**
 * Lines are separated by "\n" (the reply container uses whitespace-pre-wrap),
 * except next to headings and list items: those are blocks and already break
 * the line, so an extra "\n" would leave a blank gap.
 */
export const PartnrMarkdown = memo(function PartnrMarkdown({ content, animate = false, links = false, className }: PartnrMarkdownProps) {
  const counter: WordCounter = { next: 0 };
  const lines = content.split('\n');
  return (
    <span className={cn(className)}>
      {lines.map((text, i) => (
        <React.Fragment key={i}>
          {i > 0 && !BLOCK_LINE_RE.test(text) && !BLOCK_LINE_RE.test(lines[i - 1]) && '\n'}
          {line(text, i, counter, animate, links)}
        </React.Fragment>
      ))}
    </span>
  );
});
